import { _js } from '@ifixit/utils/localize';
import {
   Dialog,
   DialogClose,
   DialogContentPrimitive,
   DialogOverlay,
   DialogPortal,
   DialogTitle,
} from '@ifixit/ui/components/dialog';
import { FaIcon } from '@ifixit/ui/components/icon';
import { cn } from '@ifixit/ui/lib/utils';
import { useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import { createTrackedRoot } from 'Shared/react-mount-tracker';
// A guard, not a requirement: today's server-rendered pages happen to load
// the header's React entry, which imports this stylesheet already, but the
// lightbox shouldn't depend on that coincidence to render styled.
import 'Shared/tailwindcss/tailwind.css';

let launcherRoot: ReturnType<typeof createTrackedRoot> | null = null;

/**
 * Opens the lightbox from outside React, for the server-rendered pages
 * (guide view, Answers) whose click handlers live in plain JS. One root on
 * document.body serves every call; closing renders it empty rather than
 * unmounting so a later click reuses it.
 */
export function openImageLightbox(url: string) {
   const root = getLauncherRoot();
   root.render(<ImageLightbox url={url} onClose={() => root.render(null)} />);
}

function getLauncherRoot() {
   if (!launcherRoot) {
      const container = document.createElement('div');
      document.body.append(container);
      launcherRoot = createTrackedRoot(container);
   }
   return launcherRoot;
}

/**
 * The magnifying glass on a step image: opens it full frame in the browser
 * for inspecting details. Sits opposite the corner menu; a real overlay
 * button (not a menu entry) so it's one click away.
 */
export function MagnifyButton({ onClick }: { onClick: () => void }) {
   return (
      <button
         type="button"
         aria-label={_js('View full screen')}
         title={_js('View full screen')}
         // Stop the pointerdown from arming a drag on the tile.
         onPointerDown={event => event.stopPropagation()}
         onClick={onClick}
         // Touch devices have no hover to reveal the trigger, and 28px needs
         // the hit area grown past the icon rather than the icon itself.
         className="absolute left-1 top-1 flex size-7 items-center justify-center rounded-md bg-black/60 text-white opacity-0 outline-none transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-black/80 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-white/80 pointer-coarse:opacity-100 pointer-coarse:after:absolute pointer-coarse:after:-inset-2 pointer-coarse:after:content-['']"
      >
         <FaIcon name="magnifying-glass-plus" className="size-4" />
      </button>
   );
}

/**
 * The image full frame over the whole browser viewport, with a second size
 * a tap away.
 *
 * Fitting the image to the screen is the whole feature on a desktop, where
 * the page shows it in a column and the frame is far wider. On a phone the
 * page image is already full width, so a fitted view is the size it just
 * was: measured on an iPhone against a guide step, both 358px across. So
 * the image also opens to its own size, scrolled and pannable, which is
 * the part that actually magnifies.
 *
 * The backdrop closes, the image zooms. Tapping the picture used to close
 * too, which on a phone is most of the screen and the one place someone
 * reaches for to look closer.
 *
 * Zooming scrolls to the point that was tapped, so the enlarged view opens
 * on the detail someone pointed at rather than on the picture's top-left
 * corner. A keyboard press has no point, so it centers.
 */
export function ImageLightbox({
   url,
   srcSet,
   sizes,
   label,
   onClose,
}: {
   url: string;
   srcSet?: string;
   sizes?: string;
   /** The dialog's accessible name; pass what the image is on this surface. */
   label?: string;
   onClose: () => void;
}) {
   const [zoomed, setZoomed] = useState(false);
   const frameRef = useRef<HTMLDivElement>(null);
   // Where in the picture the zoom was asked for, as a fraction of its box.
   const zoomOriginRef = useRef({ x: 0.5, y: 0.5 });

   /**
    * On the image's own load, not on the render that zoomed: the zoomed
    * picture is a replacement element (see the img below), so its size is
    * only known once the file behind it is.
    */
   function scrollToZoomOrigin() {
      const frame = frameRef.current;
      if (!frame || !zoomed) {
         return;
      }
      // The frame is a scroll container now, and a scroll container starts at
      // its top-left. Put the tapped point in the middle of it instead; the
      // browser clamps whatever falls outside.
      frame.scrollLeft = zoomOriginRef.current.x * frame.scrollWidth - frame.clientWidth / 2;
      frame.scrollTop = zoomOriginRef.current.y * frame.scrollHeight - frame.clientHeight / 2;
   }

   function toggleZoom(event: MouseEvent<HTMLButtonElement>) {
      event.stopPropagation();
      const picture = event.currentTarget.getBoundingClientRect();
      // detail 0 is a keyboard activation, which has no coordinates.
      zoomOriginRef.current =
         event.detail === 0 || !picture.width || !picture.height
            ? { x: 0.5, y: 0.5 }
            : {
                 x: (event.clientX - picture.left) / picture.width,
                 y: (event.clientY - picture.top) / picture.height,
              };
      setZoomed(!zoomed);
   }

   return (
      <Dialog
         open
         onOpenChange={open => {
            if (!open) {
               onClose();
            }
         }}
      >
         <DialogPortal>
            {/* z-lightbox-overlay clears the site header (z-100), so "full frame"
                really means the whole browser viewport; content sits one tier
                above at z-lightbox so the stacking order is encoded in the values,
                not sibling render order. */}
            <DialogOverlay className="z-lightbox-overlay bg-black/90" />
            <DialogContentPrimitive
               ref={frameRef}
               aria-describedby={undefined}
               // The backdrop, not the picture or the close button: see the note above.
               onClick={event => {
                  if (event.target === event.currentTarget) {
                     onClose();
                  }
               }}
               className={cn(
                  'data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-lightbox flex outline-none',
                  // Centering a scroll container with justify/align hides the
                  // overflow past its start edge; auto margins on the child
                  // center it and still leave the whole picture reachable.
                  zoomed
                     ? 'overflow-auto p-0'
                     : 'items-center justify-center p-4 pointer-coarse:p-0'
               )}
            >
               <DialogTitle className="sr-only">{label ?? _js('Image')}</DialogTitle>
               <button
                  type="button"
                  aria-label={zoomed ? _js('Zoom out') : _js('Zoom in')}
                  onClick={toggleZoom}
                  className={cn(
                     'outline-none focus-visible:ring-[3px] focus-visible:ring-white/80',
                     zoomed ? 'm-auto block cursor-zoom-out' : 'flex cursor-zoom-in'
                  )}
               >
                  <img
                     // A new element per size, not the same one restyled: with
                     // width descriptors the browser divides the file's pixels
                     // by the density `sizes` implies, so the image reports the
                     // size it was already fitted to and max-w-none has nothing
                     // left to release. Dropping the attributes in place does
                     // not undo that, because the source selection is skipped
                     // when it lands on the url the element already has. A
                     // replacement element reads the file at its own size,
                     // which the cache serves without another request.
                     key={zoomed ? 'zoomed' : 'fitted'}
                     src={url}
                     srcSet={zoomed ? undefined : srcSet}
                     sizes={zoomed ? undefined : sizes}
                     onLoad={scrollToZoomOrigin}
                     alt=""
                     className={cn(
                        // Zoomed, the image takes its own width and the frame
                        // scrolls; max-w-none undoes the global img rule that
                        // would otherwise hold it to the viewport.
                        // Fitted, the bound is the viewport rather than the
                        // parent: a percentage would resolve against the button,
                        // whose height is its content's, leaving the button the
                        // full width of the frame with the picture letterboxed
                        // inside it. The backdrop beside the picture would then
                        // zoom instead of closing.
                        // On touch the picture runs edge to edge instead.
                        // The inset that lines the fitted view up with the
                        // page's own image is the widest a phone has to give,
                        // and the picture is what someone opened this for. The
                        // key is the pointer rather than the width: a phone
                        // turned sideways is 844px across, wider than sm.
                        zoomed
                           ? 'max-w-none'
                           : 'max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] pointer-coarse:max-h-[100dvh] pointer-coarse:max-w-[100vw]'
                     )}
                  />
               </button>
               <DialogClose
                  aria-label={_js('Close')}
                  // 44px on touch, where the guidance is a finger's width and
                  // this button sits at the edge of the screen.
                  className="fixed right-4 top-4 flex size-9 items-center justify-center rounded-md bg-black/50 text-white opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-[3px] focus-visible:ring-ring/50 pointer-coarse:size-11"
               >
                  <FaIcon name="xmark" className="size-5" />
               </DialogClose>
            </DialogContentPrimitive>
         </DialogPortal>
      </Dialog>
   );
}
